import { Grid2, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetTimeslots, editRentalDay } from '@openapi/timeslots/timeslots';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CommonButton from '../../components/atom/CommonButton';
import useNotify from '../../hooks/useNotify';
import { parseDateTimeLocal, formatDateTimeLocal } from '../../helpers/formatDateTime';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import { resolveSlotLabel, slotOrderIndex } from '../../helpers/slotTemplate';

// Split a venue-timezone "YYYY-MM-DDTHH:mm" into its date and time halves.
function splitLocal(startTime) {
    const local = formatDateTimeLocal(startTime);
    const [date, time] = local.split('T');
    return { date: date || '', time: time || '' };
}

function RentalDayEditorPage() {
    const { t } = useTranslation();
    const { showId, groupId } = useParams();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { template } = useSlotTemplate(showId);

    const { data, isLoading } = useGetTimeslots(
        { optionalId: showId, pageSize: 10000 },
        { query: { onError: notifyError } },
    );

    if (isLoading || !data) {
        return <CircularProgress />;
    }

    // The group's members, ordered by their slot's template position.
    const members = (data.timeslots || [])
        .filter((ts) => ts.timeslotGroupId === Number(groupId) && ts.slotKey)
        .sort((a, b) => slotOrderIndex(template, a.slotKey) - slotOrderIndex(template, b.slotKey));

    if (members.length === 0) {
        return (
            <PageWithTitle title={t('timeslots.editRentalDay')}>
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="error">{t('errors.notFound')}</Alert>
                </Grid2>
            </PageWithTitle>
        );
    }

    // The day (date) is shared; anchor it on the first (template-ordered) member.
    const anchor = members[0];
    const initialValues = {
        date: splitLocal(anchor.startTime).date,
        slots: members.map((m) => ({
            slotKey: m.slotKey,
            durationMinutes: m.durationMinutes ?? null,
            startTime: splitLocal(m.startTime).time,
        })),
    };

    const validateSchema = Yup.object().shape({
        date: Yup.string().required(t('common.required')),
        slots: Yup.array().of(
            Yup.object().shape({
                startTime: Yup.string().required(t('common.required')),
            }),
        ),
    });

    const onSubmit = async (values) => {
        try {
            const toIso = (timeStr) =>
                timeStr ? parseDateTimeLocal(`${values.date}T${timeStr}`) : undefined;
            await editRentalDay(Number(groupId), {
                slots: values.slots.map((s) => ({
                    slotKey: s.slotKey,
                    startTime: toIso(s.startTime),
                    ...(s.durationMinutes != null ? { durationMinutes: s.durationMinutes } : {}),
                })),
            });
            notifySuccess(t('timeslots.notifications.rentalDayUpdated'));
            navigate(`/shows/${showId}/timeslots`);
        } catch (error) {
            notifyError(error);
        }
    };

    return (
        <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validateSchema}>
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={t('timeslots.editRentalDay')}
                        headline={
                            <Grid2 display="flex" flexDirection="row" gap={2}>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        onClick={() => navigate(`/shows/${showId}/timeslots`)}
                                        label={t('common.cancel')}
                                        buttonType="secondary"
                                    />
                                </Grid2>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        disabled={isSubmitting}
                                        type="submit"
                                        label={
                                            isSubmitting ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : (
                                                t('common.save')
                                            )
                                        }
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </Grid2>
                        }
                    >
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle title={t('timeslots.editRentalDay')}>
                                <Grid2 size={{ xs: 12 }}>
                                    <Alert severity="info">
                                        {t('timeslots.editRentalDayHint')}
                                    </Alert>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label={t('timeslots.rentalDate')}
                                        value={values.date}
                                        onChange={(ev) => setFieldValue('date', ev.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={touched.date && Boolean(errors.date)}
                                        helperText={touched.date ? errors.date : ''}
                                    />
                                </Grid2>
                                {values.slots.map((slot, index) => {
                                    const slotErr = errors.slots?.[index];
                                    const slotTouched = touched.slots?.[index];
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={slot.slotKey}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {resolveSlotLabel(template, slot)}
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                type="time"
                                                label={t('timeslots.columns.startTime')}
                                                value={slot.startTime || ''}
                                                onChange={(ev) =>
                                                    setFieldValue(
                                                        `slots.${index}.startTime`,
                                                        ev.target.value,
                                                    )
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                error={
                                                    slotTouched?.startTime &&
                                                    Boolean(slotErr?.startTime)
                                                }
                                                helperText={
                                                    slotTouched?.startTime ? slotErr?.startTime : ''
                                                }
                                            />
                                        </Grid2>
                                    );
                                })}
                            </BlockWithTitle>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default RentalDayEditorPage;
